'use client';

import React, {
  createContext, useContext, useEffect,useMemo, useState
} from 'react';

import { useAuthContext } from '@/hooks/useAuthContext';

import { evaluateAllFlags } from './client';
import { Flag, FlagEntities, FlagValue } from './types';

interface FlagsContextValue {
  flags: Map<string, FlagValue>;
}

const FlagsContext = createContext<FlagsContextValue | null>( null );

interface FlagsProviderProps {
  children: React.ReactNode;
  flags: Flag<FlagValue, FlagEntities>[];
}

export function FlagsProvider( { children, flags }: FlagsProviderProps ) {
  const authContext = useAuthContext();

  const entities = useMemo( () => ( { authContext } ), [ authContext ] );

  const [ flagValues, setFlagValues ] = useState<Map<string, FlagValue>>( () => {
    return evaluateAllFlags( flags, entities );
  } );

  useEffect( () => {
    const refreshedValues = evaluateAllFlags( flags, entities );
    setFlagValues( refreshedValues );
  }, [ flags, entities ] );

  const contextValue = useMemo(
    () => ( { flags: flagValues } ),
    [ flagValues ]
  );

  return (
    <FlagsContext.Provider value={ contextValue }>
      {children}
    </FlagsContext.Provider>
  );
}

/**
 * Hook to get flag value
 * @param flag The flag to evaluate
 * @returns The current flag value
 */
export function useFlag<ValueType extends FlagValue, EntitiesType extends FlagEntities = FlagEntities>(
  flag: Flag<ValueType, EntitiesType>
): ValueType {
  const context = useContext( FlagsContext );

  if ( !context ) {
    throw new Error( 'useFlag must be used within a FlagsProvider' );
  }

  const value = context.flags.get( flag.definition.key );

  if ( value === undefined ) {
    throw new Error( `Flag "${ flag.definition.key }" not found in FlagsProvider` );
  }

  return value as ValueType;
}

/**
 * Hook to access all flag values
 * @returns Map of all flag values
 */
export function useFlags(): Map<string, FlagValue> {
  const context = useContext( FlagsContext );

  if ( !context ) {
    throw new Error( 'useFlags must be used within a FlagsProvider' );
  }

  return context.flags;
}
